/**
 * @file: useAsyncStorage.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { config } from '@euler/config';
import {
  deleteCachedFile,
  readCachedFileContents,
  writeContentsToCachedFile,
} from '@euler/lib/cache/cacheUtil';
import { makeDebug, safeMarkDevFlag } from '@euler/utils';
import { useAsyncResource, useBehaviorSubject } from '@euler/utils/hooks';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { BehaviorSubject, Subscription, concatMap, from } from 'rxjs';

const debug = makeDebug('async-resource');

const cache = new Map<string, BehaviorSubject<any>>();
const subscriptions = new Map<string, Subscription>();

export const storageKeyWrapped = (
  key: string,
  options?: { filename?: boolean },
) => {
  const s = `${config.linking.scheme}.${key}`;
  return options?.filename ? key.replace(/[|\\?*<":>+[\]]/g, '_') : s;
};

export type AsyncStorageType = 'file' | 'default';
const kFileStorePath = '__asyncstorage__';

type Options = { storageType?: AsyncStorageType };

export function clearAsyncStorageSubjectForKey(key: string) {
  cache.delete(key);
  subscriptions.get(key)?.unsubscribe();
  subscriptions.delete(key);
}

export function asyncStorageSubjectForKey$<T>(
  key: string,
  defaultValue?: T,
  options?: Options,
): BehaviorSubject<T | undefined | null> {
  if (cache.has(key)) {
    return cache.get(key) as any;
  }

  if (key == null) {
    console.error(
      new Error(
        'undefined async storage key is encountered, this must be a bug',
      ),
    );
  }

  const subject$ = new BehaviorSubject<T | undefined | null>(defaultValue);
  cache.set(key, subject$);

  const storageKey = storageKeyWrapped(key, {
    filename: options?.storageType === 'file' && Platform.OS === 'android',
  });

  if (options?.storageType === 'file' && Platform.OS === 'android') {
    const path = `${kFileStorePath}/${storageKey}`;
    readCachedFileContents(path)
      .then(async value => {
        if (value == null) {
          try {
            // try to load the value from legacy real async storage
            const fallbackKey = storageKeyWrapped(key);
            value = await AsyncStorage.getItem(fallbackKey);
            if (value != null) {
              // save the initial value.
              __DEV__ &&
                console.log(
                  'fetched fallback async storage value of key: ',
                  key,
                );
              await writeContentsToCachedFile(path, value);
            }
          } catch (e) {
            console.error('error get fallback async storage value: ', e);
          }
        }

        if (value != null) {
          subject$.next(JSON.parse(value));
        } else if (defaultValue == null) {
          subject$.next(null);
        }
      })
      .catch(e => {
        console.error(
          `error get async storage value of "${key}" from file: `,
          key,
          e,
        );
        subject$.next(null);
      })
      .finally(() => {
        const sub = subject$
          .pipe(
            concatMap(newValue =>
              from(
                newValue == null
                  ? deleteCachedFile(path, true)
                  : writeContentsToCachedFile(path, JSON.stringify(newValue)),
              ),
            ),
          )
          .subscribe();
        subscriptions.set(key, sub);
      });
  } else {
    AsyncStorage.getItem(storageKey)
      .then(value => {
        if (value != null) {
          subject$.next(JSON.parse(value));
        } else if (defaultValue == null) {
          subject$.next(null);
        }
      })
      .catch(e => {
        console.error(`error get async storage value of key "${key}": `, e);
        subject$.next(null);
      })
      .finally(() => {
        const sub = subject$
          .pipe(
            concatMap(newValue =>
              from(
                newValue == null
                  ? AsyncStorage.removeItem(storageKey)
                  : AsyncStorage.setItem(storageKey, JSON.stringify(newValue)),
              ),
            ),
          )
          .subscribe();
        subscriptions.set(key, sub);
      });
  }

  return subject$;
}

export const useAsyncStorage = <T>(
  key: string,
  defaultValue?: T,
  options?: Options,
) => {
  const subject$ = asyncStorageSubjectForKey$<T>(key, defaultValue, options);
  return useBehaviorSubject(subject$);
};

export const getAsyncStorageValue = async <T>(
  key: string,
  options?: Options,
) => {
  try {
    if (options?.storageType === 'file') {
      const path = `${kFileStorePath}/${storageKeyWrapped(key)}`;
      const data = await readCachedFileContents(path);
      if (data != null) {
        return JSON.parse(data) as T;
      }
    } else {
      const data = await AsyncStorage.getItem(storageKeyWrapped(key));
      if (data != null) {
        return JSON.parse(data) as T;
      }
    }
  } catch (e) {
    console.error('getAsyncStorageValue error: ', e);
  }
  return undefined;
};

export const useAsyncStorageValue = <T>(
  key: string,
  options?: Options,
): T | undefined => {
  return useAsyncResource(
    () => getAsyncStorageValue<T>(key, options),
    `async-storage-value:${key}`,
  );
};

const kDebug = safeMarkDevFlag(false);

if (kDebug) {
  setInterval(() => {
    debug('>>>>>>>>>>> async storage count: %d, keys: %O', cache.size, [
      ...cache.keys(),
    ]);
  }, 5000);
}
